
import { Json } from "@/integrations/supabase/types";
import { SubscriptionFeatures, SubscriptionPlan as LibSubscriptionPlan, SubscriptionPlanType, UserSubscription as LibUserSubscription } from "@/lib/types";

// Re-export types from lib/types for backwards compatibility
export type { SubscriptionFeatures };
export type { SubscriptionPlanType };
export type SubscriptionStatus = "active" | "canceled" | "expired" | "pending";

export interface SubscriptionPlan extends LibSubscriptionPlan {}

export interface UserSubscription extends LibUserSubscription {}

export interface CreateSubscriptionData {
  plan_id: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  current_period_end: string;
}

export interface AssignSubscriptionData {
  user_id: string;
  plan_id: string;
  current_period_start: string;
  current_period_end: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  is_admin_assigned?: boolean;
  is_free_assignment?: boolean;
  admin_assigned_by?: string;
}

// Helper function to convert database response to SubscriptionPlan
export const mapDbPlanToSubscriptionPlan = (data: any): SubscriptionPlan => {
  // Ensure features is properly typed and includes all feature flags
  const rawFeatures = data.features || {};
  
  const features: SubscriptionFeatures = {
    // Existing boolean feature flags
    currency_conversion: rawFeatures.currency_conversion || false,
    multiple_currencies: rawFeatures.multiple_currencies || false,
    price_comparison: rawFeatures.price_comparison || false,
    grouped_contracts: rawFeatures.grouped_contracts || false,
    contract_services: rawFeatures.contract_services || false,
    reminders: rawFeatures.reminders || false,
    payment_type_selection: rawFeatures.payment_type_selection || false,
    dashboard: rawFeatures.dashboard || false,
    contract_pdf_email: rawFeatures.contract_pdf_email || false,
    document_extraction: rawFeatures.document_extraction || false,
    
    // Numeric limits - from plan columns or features JSON
    max_contracts: data.max_contracts ?? rawFeatures.max_contracts ?? null,
    max_users: data.max_users ?? rawFeatures.max_users ?? null,
    max_erp_integrations: data.max_erp_integrations ?? rawFeatures.max_erp_integrations ?? 0,
    monthly_doc_extractions: data.monthly_doc_extractions ?? rawFeatures.monthly_doc_extractions ?? 0,
    storage_gb: data.storage_gb ?? rawFeatures.storage_gb ?? 1,
    
    // Premium feature flags
    erp_integration: rawFeatures.erp_integration || false,
    custom_fields: rawFeatures.custom_fields || false,
    calendar_view: rawFeatures.calendar_view || false,
    contract_notes: rawFeatures.contract_notes || false,
    duplicate_detection: rawFeatures.duplicate_detection || false,
    api_read: rawFeatures.api_read || false,
    api_write: rawFeatures.api_write || false,
    advanced_dashboard: rawFeatures.advanced_dashboard || false,
    // Custom plan features
    sales_analytics: rawFeatures.sales_analytics || false,
    cost_optimization: rawFeatures.cost_optimization || false,
    data_health: rawFeatures.data_health || false,
    governing_documents: rawFeatures.governing_documents || false
  };
  
  return {
    id: data.id,
    name: data.name,
    description: data.description || '',
    plan_type: data.plan_type,
    monthly_price: data.monthly_price,
    annual_price: data.annual_price || 0,
    features: features,
    is_active: data.is_active,
    show_on_landing: Boolean(data.show_on_landing),
    created_at: data.created_at,
    updated_at: data.updated_at,
    // Numeric limits from database columns
    max_contracts: data.max_contracts ?? null,
    max_users: data.max_users ?? null,
    max_erp_integrations: data.max_erp_integrations ?? 0,
    monthly_doc_extractions: data.monthly_doc_extractions ?? 0,
    storage_gb: data.storage_gb ?? 1
  };
};
